import { cx } from '@linaria/core';
import { TransitionGroup } from 'react-transition-group';
import { useGetAllRevisionsQuery, useLeaveRevision, useCourseRevisionState, RevisionSelect, useActiveRevision, useStartRevision, getFirstValidChapterDescriptorInCourse, useShareRevision, useDeleteRevision, disabledIdsToKey, useSaveRevision } from '@pm2/course-revisions';
import { useTrackingManager } from '@provider/tracking';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Tooltip, Pill } from '@ui/kit';
import { PencilIcon, ShareIcon, TrashIcon, FloppyIcon } from '@ui/kit/icons';
import { useMatchBreakpoint, HideFor, CSSRefTransition } from '@utils/react';
import { selectWrapper, select, closeEditMode, actionsWrapper, actionsTransitionDurationMs, actionsTransitionWrapper, wrapper, narrowPill } from './revisions.style.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

function Revisions(props) {
  const user = useUserIdentityState(state => state.user);
  const revisions = useGetAllRevisionsQuery(props.courseDescriptor.id);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const leaveRevision = useLeaveRevision(props.courseDescriptor);
  const showActions = user?.role === "teacher";
  const isEditing = useCourseRevisionState(state => state.isEditing);
  if (!showActions && !revisions.data?.length) {
    return null;
  }
  return /*#__PURE__*/jsxs("div", {
    className: cx(wrapper, !isMobile && showActions && "withActions"),
    children: [!!revisions.data?.length && /*#__PURE__*/jsxs("div", {
      className: selectWrapper,
      children: [/*#__PURE__*/jsx(RevisionSelect, {
        className: cx(select, isEditing && "isEditing"),
        courseDescriptor: props.courseDescriptor,
        context: "chapter"
      }), /*#__PURE__*/jsx("button", {
        onClick: () => leaveRevision(),
        className: cx(closeEditMode, isEditing && "isEditing"),
        disabled: !isEditing,
        children: "Luk tilpasning"
      })]
    }), /*#__PURE__*/jsx(HideFor, {
      breakpoint: breakpoints.mobile,
      children: (showActions || isEditing) && /*#__PURE__*/jsx(TransitionGroup, {
        component: "div",
        className: cx(actionsTransitionWrapper, revisions.data?.length && "withSelect"),
        children: /*#__PURE__*/jsx(CSSRefTransition, {
          timeout: actionsTransitionDurationMs,
          classNames: actionsWrapper,
          children: ref => /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: actionsWrapper,
            children: [!isEditing && /*#__PURE__*/jsx(DefaultActions, {
              ...props
            }), isEditing && /*#__PURE__*/jsx(EditActiveActions, {
              ...props
            }), !revisions.data?.length && /*#__PURE__*/jsx("button", {
              onClick: () => leaveRevision(),
              className: cx(closeEditMode, isEditing && "isEditing"),
              disabled: !isEditing,
              children: "Luk tilpasning"
            })]
          })
        }, `actions-${isEditing}`)
      })
    })]
  });
}
function DefaultActions(props) {
  const tracker = useTrackingManager();
  const userId = useUserIdentityState(state => state.user?.id);
  const activeRevision = useActiveRevision(props.courseDescriptor.id);
  const startRevision = useStartRevision(props.courseDescriptor.id, () => {
    if (props.currentPageDescriptor?.type !== "Course") {
      return;
    }

    // In case we're on a course frontpage, then starting a revision should
    // navigate to the first valid chapter inside.
    return getFirstValidChapterDescriptorInCourse(activeRevision, props.courseDescriptor)?.path;
  });
  const shareRevision = useShareRevision();
  const deleteRevision = useDeleteRevision(props.courseDescriptor.id);
  const editLabel = activeRevision?.id ? "Tilpas din version af forløbet" : "Tilpas forløb";
  return /*#__PURE__*/jsxs(Fragment, {
    children: [(!activeRevision || activeRevision.userId === userId) && /*#__PURE__*/jsx(Tooltip, {
      body: editLabel,
      ariaType: "label",
      placement: "top",
      children: ref => /*#__PURE__*/jsx(Pill, {
        ref: ref,
        icon: /*#__PURE__*/jsx(PencilIcon, {
          height: "14"
        }),
        iconOnly: !!activeRevision,
        onClick: () => {
          startRevision(activeRevision);
          tracker.trackEvent("userarea-delinger.revisions.start", {
            context: "left-nav"
          });
        },
        children: !activeRevision && "Tilpas"
      })
    }), activeRevision && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Tooltip, {
        placement: "top",
        body: "Del / kopi\xE9r link til versionen",
        ariaType: "label",
        children: ref => /*#__PURE__*/jsx(Pill, {
          ref: ref,
          icon: /*#__PURE__*/jsx(ShareIcon, {
            height: "22"
          }),
          onClick: () => {
            shareRevision(activeRevision);
          }
        })
      }), /*#__PURE__*/jsx(Tooltip, {
        placement: "top",
        body: "Slet denne version",
        ariaType: "label",
        children: ref => /*#__PURE__*/jsx(Pill, {
          ref: ref,
          icon: /*#__PURE__*/jsx(TrashIcon, {
            height: "20"
          }),
          onClick: () => deleteRevision(activeRevision)
        })
      })]
    })]
  });
}
function EditActiveActions(props) {
  const activeRevision = useActiveRevision(props.courseDescriptor.id);
  const canSave = useCourseRevisionState(state => disabledIdsToKey(state.disabledIds) !== disabledIdsToKey(state.editableRevision?.disabledContentIds));
  const isEditingExistingRevision = useCourseRevisionState(state => !!state.editableRevision);
  const saveRevision = useSaveRevision(props.courseDescriptor);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Tooltip, {
      body: canSave ? "Gem din version" : "Du skal tilpasse versionen før du kan gemme",
      ariaType: "description",
      placement: "top",
      children: ref => /*#__PURE__*/jsx(Pill, {
        ref: ref,
        className: narrowPill,
        icon: /*#__PURE__*/jsx(FloppyIcon, {
          height: "18"
        }),
        disabled: !canSave,
        onClick: () => saveRevision(),
        children: "Gem"
      })
    }), activeRevision && isEditingExistingRevision && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Tooltip, {
        placement: "top",
        body: "Du skal gemme din version f\xF8r du kan dele den med dine elever",
        ariaType: "description",
        children: ref => /*#__PURE__*/jsx(Pill, {
          ref: ref,
          className: narrowPill,
          icon: /*#__PURE__*/jsx(ShareIcon, {
            height: "22"
          }),
          disabled: true,
          onClick: () => {
            /* noop (always disabled) */
          },
          children: "Del"
        })
      }), /*#__PURE__*/jsx(Tooltip, {
        placement: "top",
        body: isEditingExistingRevision ? "Du skal gemme din version før du kan slette den" : "Du kan ikke slette en version som endnu ikke er blevet oprettet",
        ariaType: "description",
        children: ref => /*#__PURE__*/jsx(Pill, {
          ref: ref,
          className: narrowPill,
          icon: /*#__PURE__*/jsx(TrashIcon, {
            height: "20"
          }),
          disabled: true,
          onClick: () => {
            /* noop (always disabled) */
          },
          children: "Slet"
        })
      })]
    })]
  });
}

export { Revisions };
//# sourceMappingURL=revisions.js.map
